import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import Input from '../../components/ui/Input.jsx';
import { customersApiService } from '../../services';

function Email({ tabColors = { bgcolor: 'bg-blue-300', nocolor: 'bg-blue-100', hovercolor: 'hover:bg-blue-200' } }) {
  const { id } = useParams();

  const [inputs, setInputs] = useState({
    companyName: 'Company Name',
    emailSubject: 'Ticket# - - ',
    suggestedPageTitle: ' - - ',
    soloTicketNumber: 'Solo Ticket Number',
    ticketNumber: 'Ticket Number:',
    customerNumber: 'Customer Number',
    customerNumberRemoved: 'Customer number 200 removed',
  });

  // Fetch data from Customers API
  useEffect(() => {
    if (id) {
      customersApiService.getTicketById(id)
        .then(async ticket => {
          // Fetch fresh customer data to avoid stale cached names
          let customerName = ticket.customer_name;
          try {
            const customer = await customersApiService.getCustomerByNumber(ticket.customer_number);
            if (customer && customer.customer_name) {
              customerName = customer.customer_name;
            }
          } catch (err) {
            console.warn('Could not fetch fresh customer data, using cached name:', err);
          }

          const pageTitle = `${ticket.ticket_number} - ${customerName} - ${ticket.issue_with}`;
          setInputs({
            companyName: customerName,
            emailSubject: `${ticket.ticket_number} - ${customerName} - ${ticket.customer_number}`,
            suggestedPageTitle: pageTitle,
            soloTicketNumber: `${ticket.ticket_number}`,
            ticketNumber: `Ticket Number: ${ticket.ticket_number}`,
            customerNumber: `${ticket.customer_number}`,
            customerNumberRemoved: ticket.customer_number
              ? ticket.customer_number.replace(/\D/g, '')
              : '',
          });

          // Cambia el título del documento
          document.title = `${ticket.ticket_number} - ${customerName} - ${ticket.issue_with}`;
        })
        .catch(err => console.error('Error fetching ticket from Customers API:', err));
    } else {
      // Si no hay ID, restablece el título a su valor original
      document.title = 'SSK - Support Swiss Knife';
    }

    return () => {
      // Cuando el componente se desmonte, limpia el título
      document.title = 'SSK - Support Swiss Knife';
    };
  }, [id]);

  // Función para copiar al portapapeles
  const copyToClipboard = id => {
    const inputValue = inputs[id];
    navigator.clipboard.writeText(inputValue).then(() => {
      const messageElement = document.getElementById(`${id}-message`);
      if (messageElement) {
        messageElement.textContent = 'Copied to clipboard!';
        messageElement.classList.remove('hidden');
        setTimeout(() => {
          messageElement.textContent = '';
          messageElement.classList.add('hidden');
        }, 2000); // Limpia el mensaje después de 2 segundos
      }
    });
  };

  return (
    <div className='h-screen grid grid-cols-2 grid-rows-4 gap-4 items-center p-8'>
    {/* EMAIL SUBJECT - NOW FIRST (LEFT SIDE) */}
      <div className="bg-slate-100 rounded-xl border-2 border-blue-300 p-6">
        <div>
          <h1 className="pb-2 font-semibold text-xl flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
            Email Subject:
          </h1>
          <div className="flex">
            <Input
              id="emailSubject"
              type="text"
              value={inputs.emailSubject}
              className="text-blue-600 font-semibold text-xl"
              readOnly
            />
            <button
              onClick={() => copyToClipboard('emailSubject')}
              className="px-2 py-1 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                />
              </svg>
              Copy to Clipboard
            </button>
          </div>
          <p
            id="emailSubject-message"
            className="message bg-green-500 text-white mt-2 p-2 inline-block rounded-2xl hidden"
          ></p>
        </div>
      </div>

      {/* COMPANY NAME - NOW SECOND (RIGHT SIDE) */}
      <div className="bg-slate-100 rounded-xl border-2 border-blue-300 p-6">
        <div>
          <h1 className="pb-2 font-semibold text-xl">Company name:</h1>
          <div className="flex">
            <Input
              id="companyName"
              type="text"
              value={inputs.companyName}
              placeholder=""
              className="text-blue-600 font-semibold text-xl items-center"
              readOnly
            />
            <button
              onClick={() => copyToClipboard('companyName')}
              className="px-2 py-1 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                />
              </svg>
              Copy to Clipboard
            </button>
          </div>
          <p
            id="companyName-message"
            className="message bg-green-500 text-white mt-2 p-2 inline-block rounded-2xl hidden"
          ></p>
        </div>
      </div>
      {/* SUGGESTED PAGE TITLE - NOW THIRD (LEFT SIDE) */}
      <div className="bg-slate-100 rounded-xl border-2 border-blue-300 p-6">
        <div>
          <h1 className="pb-2 font-semibold text-xl">Page Title:</h1>
          <div className="flex">
            <Input
              id="suggestedPageTitle"
              type="text"
              value={inputs.suggestedPageTitle}
              className="text-blue-600 font-semibold text-xl"
              readOnly
            />
            <button
              onClick={() => copyToClipboard('suggestedPageTitle')}
              className="px-2 py-1 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                />
              </svg>
              Copy to Clipboard
            </button>
          </div>
          <p
            id="suggestedPageTitle-message"
            className="message bg-green-500 text-white mt-2 p-2 inline-block rounded-2xl hidden"
          ></p>
        </div>
      </div>

      <div className="bg-slate-100 rounded-xl border-2 border-blue-300 p-6">
        <div>
          <h1 className="pb-2 font-semibold text-xl">Ticket# solo:</h1>
          <div className="flex">
            <Input
              id="soloTicketNumber"
              type="text"
              value={inputs.soloTicketNumber}
              placeholder=""
              className="text-blue-600 font-semibold text-xl"
              readOnly
            />
            <button
              onClick={() => copyToClipboard('soloTicketNumber')}
              className="px-2 py-1 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                />
              </svg>
              Copy to Clipboard
            </button>
          </div>
          <p
            id="soloTicketNumber-message"
            className="message bg-green-500 text-white mt-2 p-2 inline-block rounded-2xl hidden"
          ></p>
        </div>
      </div>

      <div className="bg-slate-100 rounded-xl border-2 border-blue-300 p-6">
        <div>
          <h1 className="pb-2 font-semibold text-xl">Ticket Number:</h1>
          <div className="flex">
            <Input
              id="ticketNumber"
              type="text"
              value={inputs.ticketNumber}
              className="text-blue-600 font-semibold text-xl"
              readOnly
            />
            <button
              onClick={() => copyToClipboard('ticketNumber')}
              className="px-2 py-1 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                />
              </svg>
              Copy to Clipboard
            </button>
          </div>
          <p
            id="ticketNumber-message"
            className="message bg-green-500 text-white mt-2 p-2 inline-block rounded-2xl hidden"
          ></p>
        </div>
      </div>

      <div className="bg-slate-100 rounded-xl border-2 border-blue-300 p-6">
        <div>
          <h1 className="pb-2 font-semibold text-xl">Customer Number:</h1>
          <div className="flex">
            <Input
              id="customerNumber"
              type="text"
              value={inputs.customerNumber}
              placeholder=""
              className="text-blue-600 font-semibold text-xl"
              readOnly
            />
            <button
              onClick={() => copyToClipboard('customerNumber')}
              className="px-2 py-1 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                />
              </svg>
              Copy to Clipboard
            </button>
          </div>
          <p
            id="customerNumber-message"
            className="message bg-green-500 text-white mt-2 p-2 inline-block rounded-2xl hidden"
          ></p>
        </div>
      </div>
    </div>
  );
}

export default Email;
